/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState } from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import {
  connectionColumns as baseColumns,
  InternetConnection,
  shapeInternetData,
} from "./InternetData";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";

import { useCachedFetch } from "@hooks/useCachedFetch";
import { ListPageSkeleton, RefreshingIndicator } from "@shared/components/ListLoading";
import { useAppSelector } from "@redux/hooks";
import {
  fetchConnectionList,
} from "./requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { buildPredicate, ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import { makeInternetFields } from "./internetFieldSpec";
import { formatDataRate } from "kontrolEdge/helpers/formatDataRate";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";

interface MainInternet {
  text: string;
}

const NO_CONNECTIONS: InternetConnection[] = [];

const MainInternet: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();
  // Cached per tenant: coming back to the list paints the last answer at once and
  // refreshes it underneath.
  const {
    data: ipxList = NO_CONNECTIONS,
    error: fetchError,
    isLoading: isLoadingList,
    isRefreshing: isRefreshingList,
  } = useCachedFetch(tenantId ? `kontroledge/internet?tenantId=${tenantId}` : null, async () => {
    const { status, data } = await fetchConnectionList(api, tenantId);
    if (status >= 500 || data.success === false) {
      throw new Error(data.message || "Unknown server error");
    }
    return Array.isArray(data.data) ? shapeInternetData(data.data) : [];
  });
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);


  const internetFields = useMemo(
    () => makeInternetFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const normalized = useMemo(
    () => normalizeFilters<InternetConnection>(activeFilters, internetFields, {
      defaultStringToContains: true,
      quotedStringsExact: true,
    }),
    [activeFilters, internetFields]
  );

  const predicate = useMemo(
    () => buildPredicate<InternetConnection>(normalized, internetFields),
    [normalized, internetFields]
  );

  const filteredData = useMemo(
    () => ipxList.filter(predicate),
    [ipxList, predicate]
  );

  const suggestedKeys = useMemo(
    () => internetFields.map(f => f.label),
    [internetFields]
  );


  // const columns = baseColumns.map((col) => {
  const columns = useMemo<GenericTableColumn<InternetConnection>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: InternetConnection) => row.customer?.name ?? "",
        renderCell: (row: InternetConnection) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">ID: {row.customer.chargebeeCustomerId}</span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    // CASE A: Partner view (showing ALL customers) → the row's customer, not the lens.
    // CASE B: Customer view (only seeing your own) → the Redux-provided tenantId.
    const internetDetailPath = (row: InternetConnection) => {
      const idFromRow = Number(row.customer?.id ?? NaN);
      const nextTenantId =
        isViewingAllCustomers && Number.isFinite(idFromRow) ? idFromRow : tenantId;
      return buildDetailPath("internet", { connectionId: row.id }, { tenantId: nextTenantId });
    };

    return cols.map((col) => {
      // 1. Override the “Service ID” column to navigate on click
      if (col.header === "Connection ID") {
        return {
          ...col,
          renderCell: (row: InternetConnection) => (
            <button
              {...viewHref(internetDetailPath(row))}
              onClick={() => {
                // The tenant rides in the URL, not in history state, so a reload or a
                // pasted link keeps reading the connection in the customer's scope.
                navigate(internetDetailPath(row), { state: { connectionDetail: row } });
              }}
              className="
						hover:text-semantic-text-brand 
						hover:underline
						focus:underline 
						active:underline
						font-medium
						underline
						text-semantic-text-brand
            "
            >
              {row.subscriptionId}
            </button>
          ),
        };
      }
      // 2. Override the “Parent connection” column to navigate on click
      if (col.header === "Parent Connection") {
        return {
          ...col,
          renderCell: (row: InternetConnection) => {
            const label = row.parentConnection || "—";
            const id = row.parentConnectionId;

            if (!id || id === "-") {
              return <span className="text-semantic-text-secondary">-</span>;
            }

            // Prefer the explicit subid we shaped; fallback: parse from label "[TYPE] xyz"
            const parsedFromLabel =
              label.includes("]") ? label.split("]").slice(1).join("]").trim() : "";
            const subid = row.parentConnectionSubid ?? parsedFromLabel ?? "";

            const parentPath =
              row.parentConnectionType === "SDWAN_HUB"
                ? "/kontrol/sdwan-hub"
                : buildDetailPath("port", { portId: id }, { subid });

            return (
              <button
                {...viewHref(parentPath)}
                onClick={() => navigate(parentPath)}
                className="
                  hover:text-semantic-text-brand
                  hover:underline
                  focus:underline
                  active:underline
                  font-medium
                  underline
                  text-semantic-text-brand
                "
              >
                {label}
              </button>
            );
          },
        };
      }
      return col;
    });
  }, [baseColumns, isViewingAllCustomers]);

  const isDemo = isDemoClient();
  const internetColumnVisibility = useColumnVisibility(
    "kontrol:internet:columns",
    columns,
    ["Connection ID"],
  );
  const tableColumns = isDemo ? internetColumnVisibility.visibleColumns : columns;


  //Cases to handle the fetching and the displat of empty items
  // If data is being fetched, show only the overlay spinner.
  if (isLoadingList) {
    return <ListPageSkeleton />;
  }
  //If there's an error in the request
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  //If no ports are returned, display the empty state.
  if (ipxList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative p-1 sm:p-4 sm:overflow-hidden flex flex-col sm:h-full">
      <RefreshingIndicator active={isRefreshingList} />
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">

              <div className="mb-2 sm:mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-3 mt-0 mb-2 sm:my-5">
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder='Find by attribute or value'
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                      liveFiltering
                      debounceMs={200}
                    />
                  </div>
                  {isDemo && (
                    <ColumnVisibilityControl
                      columns={columns}
                      isVisible={internetColumnVisibility.isVisible}
                      toggle={internetColumnVisibility.toggle}
                      requiredIds={["Connection ID"]}
                    />
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <GenericTable
                  columns={tableColumns}
                  mobileCards={{
                    titleColumn: "Connection ID",
                    badgeColumn: "Connection Status",
                    // Two lines a connection (CTX-1381).
                    summary: (row) => [
                      row.customer?.name,
                      row.mbpsNum ? formatDataRate(row.mbpsNum) : null,
                    ],
                    opensFromTitle: true,
                    sortControl: true,
                    itemNoun: "connection",
                  }}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  tabletColumns={["Connection ID", "Connection Status", "Customer", "Mbps", "Parent Connection"]}
                  maxTableBodyHeight="100%"
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MainInternet;
